
import {classifyBoxScan} from './boxChampScanRouter';
import {isActionCode} from '../../utils/sharedValidationUtils';

// GROOV-4994 (hand-off timer): unified with the existing BOXNEXT/BOXPREV multi-box-nav
// action barcodes (sharedValidationUtils.js isActionCode, barcodeClassifiers.js
// checkIsMultiBoxNavigation, scanpack_item.js's legacyBoxNav guard) instead of the
// hand-off flow's own separate 'NEXTBOX' card code. Investigated before changing:
// BOXNEXT/BOXPREV are literal scanned values gated elsewhere by multi_box_shipments and,
// inside a BoxChamp view, are ALWAYS intercepted by scanpack_item.js's legacyBoxNav check
// ("Manual box navigation isn't available on a planned order...") before they could ever
// reach the classic goPrev/goNext handling — manual mid-pack box navigation was already
// unreachable from BoxChamp view, hand-off modal open or not. That interception happens
// AFTER classifyPausedInput runs, so returning {action:'confirm'} here for BOXNEXT while
// the hand-off is paused pre-empts it cleanly: the modal closes before legacyBoxNav is
// ever reached. Outside a pending hand-off (HANDOFF_IDLE) this file's own idle-branch
// check below still rejects a premature BOXNEXT with 'box_incomplete' exactly as it did
// for the old NEXTBOX code, so nothing new leaks into the legacy goPrev/goNext path.
const BOXNEXT_ACTION_CODE = 'BOXNEXT';

export const HANDOFF_IDLE = 'idle';
export const HANDOFF_AWAITING_FIRST_VERIFY = 'awaiting_first_verify';
export const HANDOFF_PAUSED = 'paused';

const idle = {
  kind: HANDOFF_IDLE,
  sealBox: null,
  nextBox: null,
  expectedBox: null,
  isLastBox: false,
  requiresBoxScan: false,
  activeBoxName: null,
};

const activeNameOf = view => {
  if (!view || !view.zones || !view.zones.active) return null;
  return view.zones.active.box ? view.zones.active.box.name : null;
};

export function handoffState({
  view,
  verificationOn = false,
  verifiedBoxIds = [],
  justConfirmedBoxId = null,
  autoSealedBoxIds = [],
  sealConfirmedBoxIds = [],
  modalOpen = false,
} = {}) {
  if (!view || !view.projection || view.projection.totalBoxes === 0) return idle;
  const activeBoxName = activeNameOf(view);
  const idleHere = {...idle, activeBoxName};
  if (modalOpen) return idleHere;

  const boxes = view.projection.boxes;

  const autoSealed = new Set(autoSealedBoxIds || []);
  const sealConfirmed = new Set(
    (sealConfirmedBoxIds || []).filter(id => {
      const entry = boxes.find(e => e.box.id === id);
      return !entry || entry.complete;
    }),
  );
  const pendingIndex = boxes.findIndex(
    entry =>
      entry.target > 0 &&
      entry.complete &&
      !sealConfirmed.has(entry.box.id) &&
      (!entry.sealed || autoSealed.has(entry.box.id)) &&
      entry.box.id !== justConfirmedBoxId,
  );
  if (pendingIndex === -1 && verificationOn && !(verifiedBoxIds || []).length) {
    const first = boxes.find(e => e.target > 0 && !e.complete && !e.sealed);
    if (first) {
      return {
        ...idleHere,
        kind: HANDOFF_AWAITING_FIRST_VERIFY,
        expectedBox: first.box,
        requiresBoxScan: Boolean(first.box && first.box.barcode),
      };
    }
  }

  if (pendingIndex === -1) return idleHere;

  const sealBox = boxes[pendingIndex].box;
  const nextEntry = boxes.slice(pendingIndex + 1).find(entry => entry.target > 0) || null;
  const nextBox = nextEntry ? nextEntry.box : null;

  return {
    kind: HANDOFF_PAUSED,
    activeBoxName,
    sealBox,
    nextBox,
    expectedBox: verificationOn ? nextBox : null,
    isLastBox: nextBox === null,
    requiresBoxScan: Boolean(verificationOn && nextBox && nextBox.barcode),
  };
}

const norm = value => (value == null ? '' : String(value).trim().toUpperCase());

export function classifyPausedInput(
  value,
  {state, catalog, unboxedBarcodes = [], settings = {}} = {},
) {
  const scanned = norm(value);
  const kind = state ? state.kind : HANDOFF_IDLE;

  if (kind === HANDOFF_IDLE) {
    // GROOV-4994 (hand-off timer): the old dedicated 'NEXTBOX' card had exactly one
    // meaning, so scanning it before the box was actually done was unambiguously "too
    // early" and got its own 'box_incomplete' message here. BOXNEXT is not
    // single-purpose the same way — inside a BoxChamp view it is scanpack_item.js's
    // legacyBoxNav guard ("Manual box navigation isn't available on a planned order...")
    // that already owns refusing it with the correct neutral (non-error) tone whenever
    // no hand-off is pending (GROOV-4994 defect cleanup #1 — regression-pinned in
    // scanPackItemGuided.spec.js: "plays the neutral setAside tone... on a refused
    // BOXPREV/BOXNEXT nav"). Special-casing it here too would pre-empt that guard with a
    // different message and the error buzzer instead of the neutral tone, so BOXNEXT is
    // deliberately left to fall through to pass_through, same as any other value, while
    // idle. It only gets hand-off meaning once a hand-off is actually pending, below.
    return {action: 'pass_through'};
  }

  if ((unboxedBarcodes || []).some(barcode => norm(barcode) === scanned)) {
    return {action: 'pass_through'};
  }

  if (scanned !== BOXNEXT_ACTION_CODE && isActionCode(value, settings)) {
    return {action: 'pass_through'};
  }

  if (state.requiresBoxScan) {
    // GROOV-4994 (defect 4): settings already reaches this function (used just above for
    // isActionCode); forward it so classifyBoxScan applies the tenant's barcode cleanup
    // instead of matching against a merely-trimmed value. Pass the raw `value`, not the
    // already-normed `scanned` local, so classifyBoxScan performs its own cleanup+norm
    // exactly once instead of stacking two different partial normalizations.
    const result = classifyBoxScan(value, {expectedBox: state.expectedBox, catalog, settings});
    if (result.kind === 'confirm_expected') return {action: 'confirm', boxType: result.boxType};
    if (scanned === BOXNEXT_ACTION_CODE) {
      return {
        action: 'reject',
        reason: 'scan_the_box',
        expectedName: state.expectedBox ? state.expectedBox.name : null,
      };
    }
    if (result.kind !== 'none') {
      return {
        action: 'reject',
        reason: 'wrong_box',
        expectedName: state.expectedBox ? state.expectedBox.name : null,
        boxType: result.boxType,
      };
    }
    return {
      action: 'reject',
      reason: 'scan_the_box',
      expectedName: state.expectedBox ? state.expectedBox.name : null,
    };
  }

  if (scanned === BOXNEXT_ACTION_CODE) return {action: 'confirm'};

  return {
    action: 'reject',
    reason: 'finish_handoff',
    expectedName: state.sealBox ? state.sealBox.name : null,
  };
}
